import { PostHogProvider, usePostHog } from '@posthog/react'
import { type ReactNode, useEffect, useRef } from 'react'
import { useAuth } from '@/state/AuthContext'
import { productAnalyticsPrivacyOptions } from '@/lib/productAnalytics'

const POSTHOG_DEFAULT_HOST = 'https://us.i.posthog.com'

const posthogProjectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim()
const posthogHost =
  import.meta.env.VITE_POSTHOG_HOST?.trim() || POSTHOG_DEFAULT_HOST
const posthogOptions = {
  api_host: posthogHost,
  defaults: '2026-01-30',
  ...productAnalyticsPrivacyOptions,
} as const

/**
 * PostHog starts here, when the provider first renders, never at module load: main.tsx takes an invite link's token
 * out of the address bar first (inviteArrival), and PostHog records the address it starts on. Keep it that way.
 */
export function ProductAnalyticsProvider({
  children,
}: {
  children: ReactNode
}) {
  if (!posthogProjectToken) {
    return <>{children}</>
  }

  return (
    <PostHogProvider apiKey={posthogProjectToken} options={posthogOptions}>
      {children}
    </PostHogProvider>
  )
}

export function ProductAnalyticsIdentity() {
  const { isAuthenticated, organization, user } = useAuth()
  const posthog = usePostHog()
  const identifiedUserId = useRef<string | null>(null)

  useEffect(() => {
    if (!posthogProjectToken || !posthog?.__loaded) {
      return
    }

    if (!isAuthenticated || !user) {
      if (identifiedUserId.current) {
        posthog.reset()
        identifiedUserId.current = null
      }
      return
    }

    const personProperties: Record<string, string> = {}

    if (user.attributes.role) {
      personProperties.role = user.attributes.role
    }

    if (organization) {
      personProperties.organization_id = organization.id
      personProperties.organization_name = organization.attributes.name
    }

    posthog.identify(user.id, personProperties)
    identifiedUserId.current = user.id

    if (organization) {
      posthog.group('organization', organization.id, {
        name: organization.attributes.name,
      })
    }
  }, [isAuthenticated, organization, posthog, user])

  return null
}
